import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Button, Alert, CircularProgress, FormGroup, Breadcrumbs, Link, Snackbar } from '@mui/material';
import { firmService } from '../services/firmService';
import { projectService } from '../services/projectService';
import { DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES } from '../constants/caseStatusNotifications';
import { dispositionOptions } from '../constants/dispositions';
import ProjectForm, { ProjectFormValues } from '../components/ProjectForm';
import { handleApiError, scrollToFirstError } from '../utils/formErrorHandling';

const ProjectCreate: React.FC = () => {
  const navigate = useNavigate();
  const { firmId } = useParams<{ firmId: string }>();

  const [form, setForm] = useState<ProjectFormValues>({
    name: '',
    description: '',
    enable_checklist: false,
    checklist_instructions: '',
    checklist_files: [],
    billing_type: '',
    rate: '',
    flat_rate: '',
    flat_rate_page_from: '',
    flat_rate_page_to: '',
    variable_rate: '',
    variable_rate_page_from: '',
    variable_rate_page_to: '',
    additional_claimant: '',
    appendix_c_rate: '',
    appendix_c_rate_ranges: [],
    free_cases_allowed: '0',
    hide_case_timer: false,
    enable_estimate_approval: false,
    max_estimate_hours: '',
    enable_sample_estimate: false,
    max_capacity_pages_per_hour: '0',
    remind_on_file_missing: false,
    due_date_required: true,
    notify_admins_on_case_status_change: false,
    case_status_change_notification_statuses: [...DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES],
    enable_disposition: false,
    mandatory_disposition_field: false,
    disposition_list: [],
    status: 'active',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [firmName, setFirmName] = useState('');
  
  // Enhanced toast/snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'error' | 'warning' | 'info' | 'success'
  });
  
  // Field-specific error state and refs
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  
  // Helper function to show toast messages
  const showToast = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // Helper function to close toast
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  useEffect(() => {
    const loadFirm = async () => {
      if (!firmId) return;
      try {
        const res = await firmService.getFirmById(parseInt(firmId));
        setFirmName(res.data.name);
      } catch {}
    };
    loadFirm();
  }, [firmId]);

  const handleSubmitForm = async (values: ProjectFormValues, overrideStatus?: 'active' | 'draft') => {
    if (!firmId) {
      showToast('Missing firm information', 'error');
      setError('Missing firm');
      return;
    }
    
    // Clear previous errors and show loading state
    setFieldErrors({});
    setError('');
    handleCloseSnackbar();
    
    // Validate required fields
    const validationErrors: Record<string, string[]> = {};
    
    if (!values.name.trim()) {
      validationErrors.name = ['Project title is required'];
    }
    if (!values.billing_type) {
      validationErrors.billing_type = ['Please select a billing type'];
    }
    
    // Validation for Flat Rate billing
    if (values.billing_type === 'Flat Rate') {
      if (!values.flat_rate || Number(values.flat_rate) <= 0) {
        validationErrors.flat_rate = ['Flat rate must be a positive number'];
      }
      if (!values.variable_rate || Number(values.variable_rate) <= 0) {
        validationErrors.variable_rate = ['Variable rate must be a positive number'];
      }
      if (!values.flat_rate_page_from || Number(values.flat_rate_page_from) < 0) {
        validationErrors.flat_rate_page_from = ['Required field'];
      }
      if (!values.flat_rate_page_to || Number(values.flat_rate_page_to) <= 0) {
        validationErrors.flat_rate_page_to = ['Required field'];
      }
      if (!values.variable_rate_page_from || Number(values.variable_rate_page_from) < 0) {
        validationErrors.variable_rate_page_from = ['Required field'];
      }
    } else {
      // Validation for other billing types
      if (!values.rate || Number(values.rate) <= 0) {
        validationErrors.rate = ['Rate must be a positive number'];
      }
      if (values.billing_type === 'Page Based' && values.appendix_c_rate && Number(values.appendix_c_rate) < 0) {
        validationErrors.appendix_c_rate = ['Appendix C Rate cannot be negative'];
      }
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      scrollToFirstError(validationErrors, fieldRefs, showToast);
      setError('Please correct the errors below and try again.');
      return;
    }
    
    try {
      setSubmitting(true);
      await projectService.createProject({
        name: values.name,
        description: values.description,
        firm_id: parseInt(firmId),
        billing_type: values.billing_type as 'Page Based' | 'Case Based' | 'Time Based' | 'Flat Rate' | 'Consolidation',
        rate: values.billing_type !== 'Flat Rate' ? Number(values.rate) : 0,
        flat_rate: values.billing_type === 'Flat Rate' ? Number(values.flat_rate) : undefined,
        flat_rate_page_from: values.billing_type === 'Flat Rate' ? Number(values.flat_rate_page_from) : undefined,
        flat_rate_page_to: values.billing_type === 'Flat Rate' ? Number(values.flat_rate_page_to) : undefined,
        variable_rate: values.billing_type === 'Flat Rate' ? Number(values.variable_rate) : undefined,
        variable_rate_page_from: values.billing_type === 'Flat Rate' ? Number(values.variable_rate_page_from) : undefined,
        variable_rate_page_to: values.billing_type === 'Flat Rate' && values.variable_rate_page_to ? Number(values.variable_rate_page_to) : undefined,
        additional_claimant: values.billing_type === 'Flat Rate' && values.additional_claimant ? Number(values.additional_claimant) : undefined,
        appendix_c_rate: (values.billing_type === 'Consolidation' || values.billing_type === 'Page Based') && values.appendix_c_rate
          ? Number(values.appendix_c_rate)
          : undefined,
        appendix_c_rate_ranges: values.billing_type === 'Page Based' && values.appendix_c_rate_ranges?.length
          ? values.appendix_c_rate_ranges
              .filter((r) => r.rate)
              .map((r) => ({
                from: Number(r.from || 0),
                to: r.to ? Number(r.to) : null,
                rate: Number(r.rate),
              }))
          : undefined,
        free_cases_allowed: Number(values.free_cases_allowed || '0'),
        hide_case_timer: values.hide_case_timer,
        enable_checklist: values.enable_checklist,
        checklist_instructions: values.enable_checklist ? values.checklist_instructions : undefined,
        checklist_files: values.enable_checklist ? values.checklist_files : undefined,
        enable_estimate_approval: values.enable_estimate_approval,
        max_estimate_hours: values.max_estimate_hours ? Number(values.max_estimate_hours) : undefined,
        enable_sample_estimate: values.enable_sample_estimate,
        max_capacity_pages_per_hour: values.enable_sample_estimate ? Number(values.max_capacity_pages_per_hour || '0') : undefined,
        remind_on_file_missing: values.remind_on_file_missing,
        allow_optional_due_date: !values.due_date_required,
        notify_admins_on_case_status_change: values.notify_admins_on_case_status_change,
        case_status_change_notification_statuses: values.case_status_change_notification_statuses,
        enable_disposition: values.enable_disposition,
        mandatory_disposition_field: values.enable_disposition ? values.mandatory_disposition_field : undefined,
        disposition_list: values.enable_disposition ? values.disposition_list : undefined,
        body_format: 'Full HTML',
        checklist_instructions_format: values.enable_checklist ? 'Full HTML' : undefined,
        status: overrideStatus ?? values.status,
      });
      showToast('Project created successfully! Redirecting...', 'success');
      setTimeout(() => navigate(`/firms/${firmId}`), 1500);
    } catch (err: any) {
      handleApiError(
        err,
        setFieldErrors,
        setError,
        fieldRefs,
        'Failed to create project. Please try again.',
        showToast
      );
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Box>
      {firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            sx={{ textDecoration: 'none' }}
          >
            Firms
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {firmName || `Firm #${firmId}`}
          </Link>
          <Typography color="text.primary">Create Project</Typography>
        </Breadcrumbs>
      )}
      <Typography variant="h4" gutterBottom>
        Create Project
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Cards layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <ProjectForm
          initialValues={form}
          submitting={submitting}
          mode="create"
          onSubmit={handleSubmitForm}
          onCancel={() => navigate(-1)}
          enableFileUpload
          fieldErrors={fieldErrors}
          fieldRefs={fieldRefs}
        />
      </Box>

      {/* Enhanced Toast/Snackbar for better error messaging */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectCreate;
